export const WIR_DATE_FORMAT = 'yyyy/MM/dd';

export const WIR_DATE_FORMAT_WITH_TIME = 'yyyy-MM-dd HH:mm';

export enum InterventionComplexityType {
  NON_ROUTINE_1 = 'Non Routine (1)',
  NON_ROUTINE_2 = 'Non Routine (2)',
  ROUTINE_3 = 'Routine (3)',
  ROUTINE_4_REACTIVE = 'Routine (4) (reactive)',
}

export enum PriorityType {
  NORMAL = 'Normal',
  P1 = 'P1',
  P2 = 'P2',
  P3 = 'P3',
}

export enum ScopeType {
  GENERAL = 'General',
  INTEGRITY = 'Integrity',
  PRODUCTIVITY = 'Productivity',
  GEOSCIENCE = 'Geoscience',
}

const WIRPhaseInitStarter = {
  startedOn: undefined,
  due: undefined,
  completed: undefined,
  assignedTo: '',
};

const WIRSectionInitStarter = {
  isTicked: false,
  lastUpdatedDate: new Date().toISOString(),
  completedDate: undefined,
  assignedTo: '',
  completedBy: '',
};

export const newWirInit = {
  wellId: '',
  wirNumber: '',
  dueDateStatus: '',
  state: '',
  startDate: '',
  endDate: '',
  lastUpdatedDate: undefined,
  lastUpdatedBy: '',
  well: '',
  isUnplannedReactiveScope: false,
  expectedEngineeringTime: '',
  assignees: [],
  phases: {
    wirCreated: {
      ...WIRPhaseInitStarter,
      sections: {
        initialRequestInformation: {
          ...WIRSectionInitStarter,
          interventionTitle: '',
          originatorId: '',
          objectives: '',
          originatorName: '',
          interventionDate: null,
          interventionType: [],
          interventionComplexity: '',
        },
        organiseKickOffMeeting: {
          ...WIRSectionInitStarter,
          meetingDate: undefined,
        },
      },
    },
  },
  signOffs: {},
  actions: [],
  checklist: {
    generic: [],
    production_and_reservoir: [],
    pumping_operation: [],
    perforations: [],
  },
};

export enum WIRJounerySteps {
  wirCreated = 'wirCreated',
  kickOffTechnicalWir = 'kickOffTechnicalWir',
  engineeringWork = 'engineeringWork',
  finalWir = 'finalWir',
  finalPreparation = 'finalPreparation',
  reactiveScope = 'reactiveScope',
  interventionOperation = 'interventionOperation',
  interventionCloseOut = 'interventionCloseOut',
}

export type Phases = (typeof WIRJounerySteps)[keyof typeof WIRJounerySteps];

export enum WIRJouneryStepsNg {
  wirCreated = 'wirCreated',
  kickOffTechnicalWir = 'kickOffTechnicalWir',
  engineeringWork = 'engineeringWork',
  finalWir = 'finalWir',
  reactiveScope = 'reactiveScope',
  ngFinalPreparation = 'ngFinalPreparation',
  ngInterventionOperation = 'ngInterventionOperation',
  ngInterventionCloseOut = 'ngInterventionCloseOut',
}

export type PhasesNg = (typeof WIRJouneryStepsNg)[keyof typeof WIRJouneryStepsNg];

export enum WIRJouneryStepsIr {
  wirCreated = 'wirCreated',
  kickOffTechnicalWir = 'kickOffTechnicalWir',
  iraqEngineeringWork = 'iraqEngineeringWork',
  iraqFinalWir = 'iraqFinalWir',
  reactiveScope = 'reactiveScope',
  iraqFinalPreparation = 'iraqFinalPreparation',
  iraqInterventionOperation = 'iraqInterventionOperation',
  iraqInterventionCloseOut = 'iraqInterventionCloseOut',
}

export type PhasesIr = (typeof WIRJouneryStepsIr)[keyof typeof WIRJouneryStepsIr];

export enum WIRJouneryStepsAr {
  wirCreated = 'wirCreated',
  kickOffTechnicalWir = 'kickOffTechnicalWir',
  argentinaEngineeringWork = 'argentinaEngineeringWork',
  argentinaFinalWir = 'argentinaFinalWir',
  reactiveScope = 'reactiveScope',
  argentinaFinalPreparation = 'argentinaFinalPreparation',
  argentinaInterventionOperation = 'argentinaInterventionOperation',
  argentinaInterventionCloseOut = 'argentinaInterventionCloseOut',
}

export type PhasesAr = (typeof WIRJouneryStepsAr)[keyof typeof WIRJouneryStepsAr];

export enum WIRJouneryStepsCg {
  cgWirCreated = 'cgWirCreated',
  cgKickOffTechnicalWir = 'cgKickOffTechnicalWir',
  cgEngineeringWork = 'cgEngineeringWork',
  cgFinalWir = 'cgFinalWir',
  reactiveScope = 'reactiveScope',
  cgFinalPreparation = 'cgFinalPreparation',
  cgInterventionOperation = 'cgInterventionOperation',
  cgInterventionCloseOut = 'cgInterventionCloseOut',
}

export type PhasesCg = (typeof WIRJouneryStepsCg)[keyof typeof WIRJouneryStepsCg];

export enum WIRJouneryStepsDk {
  stageGate1 = 'stageGate1',
  stageGate2 = 'stageGate2',
  stageGate3 = 'stageGate3',
  stageGate4 = 'stageGate4',
  stageGate5 = 'stageGate5',
}

export type PhasesDk = (typeof WIRJouneryStepsDk)[keyof typeof WIRJouneryStepsDk];

export const WIRCreatedSec = {
  initialRequestInformation: 'initialRequestInformation',
  organiseKickOffMeeting: 'organiseKickOffMeeting',
} as const;

export type WIRCreatedSecKey = keyof typeof WIRCreatedSec;

export const WIRKofMeetingTechnicalWirSec = {
  budget: 'budget',
  wellInformation: 'wellInformation',
  reviewWellHistory: 'reviewWellHistory',
  reviewChecklistActions: 'reviewChecklistActions',
  signOffTechnicalWIR: 'signOffTechnicalWIR',
} as const;

export type WIRKofMeetingTechnicalWirSecKey = keyof typeof WIRKofMeetingTechnicalWirSec;

export const WIREngineeringWorkSec = {
  vendorEngagementSelection: 'vendorEngagementSelection',
  equipmentIdentification: 'equipmentIdentification',
  aroRlo: 'aroRlo',
  engineeringAnalysis: 'engineeringAnalysis',
  equipmentProcurement: 'equipmentProcurement',
  reviewChecklistActions: 'reviewChecklistActions',
  interventionStrategyDefined: 'interventionStrategyDefined',
  chemicalChecksTopsideMetallurgy: 'chemicalChecksTopsideMetallurgy',
  crossChemicalCompatibility: 'crossChemicalCompatibility',
  chemicalChecksReservoir: 'chemicalChecksReservoir',
  chemicalChecksFormation: 'chemicalChecksFormation',
  chemicalChecksTopsideEffluent: 'chemicalChecksTopsideEffluent',
  chemicalChecksFlowBack: 'chemicalChecksFlowBack',
  cefasRegistration: 'cefasRegistration',
  sandingStudy: 'sandingStudy',
  technicalReviewRiskReward: 'technicalReviewRiskReward',
  technicalPosEstabilished: 'technicalPosEstabilished',
  hazopCarriedOut: 'hazopCarriedOut',
  afeCreated: 'afeCreated',
  afeGhgEmission: 'afeGhgEmission',
  programDraft: 'programDraft',
} as const;

export type WIREngineeringWorkSecKey = keyof typeof WIREngineeringWorkSec;

export const WIRFinalWIRSec = {
  managerialSummary: 'managerialSummary',
  assignInterventionSPDT: 'assignInterventionSPDT',
  finalWirSignOff: 'finalWirSignOff',
} as const;

export type WIRFinalWIRSecKey = keyof typeof WIRFinalWIRSec;

export const WIRFinalPreparationSec = {
  afeApproved: 'afeApproved',
  regularPermitsRequested: 'regularPermitsRequested',
  partnersNotified: 'partnersNotified',
  updateChemicalPermits: 'updateChemicalPermits',
  wonsApplication: 'wonsApplication',
  hseNotification: 'hseNotification',
  fpsWaiver: 'fpsWaiver',
  bridgingSimopsDocuments: 'bridgingSimopsDocuments',
  wellInterventionOnPaper: 'wellInterventionOnPaper',
  perforationProforma: 'perforationProforma',
  equipmentPreparation: 'equipmentPreparation',
  programmeReview: 'programmeReview',
  hqTechnicalReview: 'hqTechnicalReview',
  programmeDistributed: 'programmeDistributed',
  costSheetPrepared: 'costSheetPrepared',
  vesselApplication: 'vesselApplication',
} as const;

export type WIRFinalPreparationSecKey = keyof typeof WIRFinalPreparationSec;

export const WIRInterventionOperationSec = {
  assetStatusCheck: 'assetStatusCheck',
  servicePartnerPaRoleIdentification: 'servicePartnerPaRoleIdentification',
  regularPermitsReceived: 'regularPermitsReceived',
  equipmentPersonnelSite: 'equipmentPersonnelSite',
  capPortalUpdates: 'capPortalUpdates',
  wellHandoverIntervStart: 'wellHandoverIntervStart',
  emergencyDrill: 'emergencyDrill',
  lessonsCaptured: 'lessonsCaptured',
  prepareTpfr: 'prepareTpfr',
  livePerformanceTracking: 'livePerformanceTracking',
  wellHandoverBackToOpsEnd: 'wellHandoverBackToOpsEnd',
} as const;

export type WIRInterventionOperationSecKey = keyof typeof WIRInterventionOperationSec;

export const WIRInterventionCloseOutSec = {
  interventionRetrospective: 'interventionRetrospective',
  closeOutChecklists: 'closeOutChecklists',
  interventionSingleSlideSummary: 'interventionSingleSlideSummary',
  capPortalCloseOut: 'capPortalCloseOut',
  wonsCloseOut: 'wonsCloseOut',
  signOffWcrs: 'signOffWcrs',
  lessonsCaptured: 'lessonsCaptured',
  completionSchematicUpdate: 'completionSchematicUpdate',
  wellSchematicUpdate: 'wellSchematicUpdate',
  emtraxUpdate: 'emtraxUpdate',
  interventionWellDataStored: 'interventionWellDataStored',
  vendorCloseOut: 'vendorCloseOut',
  interventionPerformanceCloseOut: 'interventionPerformanceCloseOut',
  assetCloseOutPresentation: 'assetCloseOutPresentation',
  rex: 'rex',
  costConsolidationCloseOut: 'costConsolidationCloseOut',
  endOfInterventionReport: 'endOfInterventionReport',
  hqTechnicalfinalInterventionSignOff: 'hqTechnicalfinalInterventionSignOff',
  actualGhgEmission: 'actualGhgEmission',
} as const;

export type WIRInterventionCloseOutSecKey = keyof typeof WIRInterventionCloseOutSec;

export const WIRReactiveScopeSec = {
  initialRequestInformation: 'initialRequestInformation',
  organiseKickOffMeeting: 'organiseKickOffMeeting',
  reviewChecklistActions: 'reviewChecklistActions',
  wellInformation: 'wellInformation',
  vendorEngagementSelection: 'vendorEngagementSelection',
  equipmentIdentification: 'equipmentIdentification',
  interventionStrategyDefined: 'interventionStrategyDefined',
  afeApproved: 'afeApproved',
  afeGhgEmission: 'afeGhgEmission',
  managerialSummary: 'managerialSummary',
  vesselApplication: 'vesselApplication',
  finalWirSignOff: 'finalWirSignOff',
  equipmentPreparation: 'equipmentPreparation',
  programmeReview: 'programmeReview',
  programmeDistributed: 'programmeDistributed',
  chemicalChecksTopsideMetallurgy: 'chemicalChecksTopsideMetallurgy',
  crossChemicalCompatibility: 'crossChemicalCompatibility',
  chemicalChecksReservoir: 'chemicalChecksReservoir',
  chemicalChecksFormation: 'chemicalChecksFormation',
  chemicalChecksTopsideEffluent: 'chemicalChecksTopsideEffluent',
  chemicalChecksFlowBack: 'chemicalChecksFlowBack',
  cefasRegistration: 'cefasRegistration',
  sandingStudy: 'sandingStudy',
  updateChemicalPermits: 'updateChemicalPermits',
  wonsApplication: 'wonsApplication',
  hseNotification: 'hseNotification',
  fpsWaiver: 'fpsWaiver',
  bridgingSimopsDocuments: 'bridgingSimopsDocuments',
  perforationProforma: 'perforationProforma',
} as const;

export type WIRReactiveScopeSecKey = keyof typeof WIRReactiveScopeSec;

export const WIRStageGate1Sec = {
  interventionJustification: 'interventionJustification',
} as const;

export type WIRStageGate1SecKey = keyof typeof WIRStageGate1Sec;

export const WIRStageGate2Sec = {
  initialRequestInformation: 'initialRequestInformation',
  wellInformation: 'wellInformation',
  reviewWellHistory: 'reviewWellHistory',
  reviewChecklistActions: 'reviewChecklistActions',
  organiseKickOffMeeting: 'organiseKickOffMeeting',
  signOffTechnicalWIR: 'signOffTechnicalWIR',
} as const;

export type WIRStageGate2SecKey = keyof typeof WIRStageGate2Sec;

export const WIRStageGate3Sec = {
  equipmentIdentification: 'equipmentIdentification',
  vendorEngagementSelection: 'vendorEngagementSelection',
  interventionStrategyDefined: 'interventionStrategyDefined',
  technicalReviewRiskReward: 'technicalReviewRiskReward',
  technicalPosEstabilished: 'technicalPosEstabilished',
  afeCreated: 'afeCreated',
  afeGhgEmission: 'afeGhgEmission',
  partnersNotified: 'partnersNotified',
  afeApproved: 'afeApproved',
  wellInterventionOnPaper: 'wellInterventionOnPaper',
  equipmentPreparation: 'equipmentPreparation',
  hqTechnicalReview: 'hqTechnicalReview',
  programmeReview: 'programmeReview',
  costSheetPrepared: 'costSheetPrepared',
  programmeDistributed: 'programmeDistributed',
  sandingStudy: 'sandingStudy',
  vesselApplication: 'vesselApplication',
  reviewChecklistActions: 'reviewChecklistActions',
  managerialSummary: 'managerialSummary',
  wonsApplication: 'wonsApplication',
  fpsWaiver: 'fpsWaiver',
  bridgingSimopsDocuments: 'bridgingSimopsDocuments',
  hseNotification: 'hseNotification',
  perforationProforma: 'perforationProforma',
  chemicalChecksTopsideMetallurgy: 'chemicalChecksTopsideMetallurgy',
  crossChemicalCompatibility: 'crossChemicalCompatibility',
  chemicalChecksReservoir: 'chemicalChecksReservoir',
  chemicalChecksFormation: 'chemicalChecksFormation',
  chemicalChecksTopsideEffluent: 'chemicalChecksTopsideEffluent',
  chemicalChecksFlowBack: 'chemicalChecksFlowBack',
  cefasRegistration: 'cefasRegistration',
  updateChemicalPermits: 'updateChemicalPermits',
  finalWirSignOff: 'finalWirSignOff',
} as const;

export type WIRStageGate3SecKey = keyof typeof WIRStageGate3Sec;

export const WIRStageGate4Sec = {
  equipmentPersonnelSite: 'equipmentPersonnelSite',
  wellHandoverIntervStart: 'wellHandoverIntervStart',
  emergencyDrill: 'emergencyDrill',
  lessonsCaptured: 'lessonsCaptured',
  prepareTpfr: 'prepareTpfr',
  livePerformanceTracking: 'livePerformanceTracking',
  wellHandoverBackToOpsEnd: 'wellHandoverBackToOpsEnd',
} as const;

export type WIRStageGate4SecKey = keyof typeof WIRStageGate4Sec;

export const WIRStageGate5Sec = {
  interventionRetrospective: 'interventionRetrospective',
  closeOutChecklists: 'closeOutChecklists',
  interventionSingleSlideSummary: 'interventionSingleSlideSummary',
  wonsCloseOut: 'wonsCloseOut',
  completionSchematicUpdate: 'completionSchematicUpdate',
  wellSchematicUpdate: 'wellSchematicUpdate',
  interventionWellDataStored: 'interventionWellDataStored',
  vendorCloseOut: 'vendorCloseOut',
  interventionPerformanceCloseOut: 'interventionPerformanceCloseOut',
  assetCloseOutPresentation: 'assetCloseOutPresentation',
  rex: 'rex',
  costConsolidationCloseOut: 'costConsolidationCloseOut',
  endOfInterventionReport: 'endOfInterventionReport',
  hqTechnicalfinalInterventionSignOff: 'hqTechnicalfinalInterventionSignOff',
} as const;

export type WIRStageGate5SecKey = keyof typeof WIRStageGate5Sec;

export enum WIRCreateJourneyStepsKeys {
  // United Kingdom
  WIR_CREATED = 'wirCreated',
  KICK_OFF_TECHNICAL_WIR = 'kickOffTechnicalWir',
  ENGINEERING_WORK = 'engineeringWork',
  FINAL_WIR = 'finalWir',
  FINAL_PREPARATION = 'finalPreparation',
  REACTIVE_SCOPE = 'reactiveScope',
  INTERVENTION_OPERATION = 'interventionOperation',
  INTERVENTION_CLOSE_OUT = 'interventionCloseOut',
  // Denmark
  STAGE_GATE_1 = 'stageGate1',
  STAGE_GATE_2 = 'stageGate2',
  STAGE_GATE_3 = 'stageGate3',
  STAGE_GATE_4 = 'stageGate4',
  STAGE_GATE_5 = 'stageGate5',
  // Nigeria
  NG_FINAL_PREPARATION = 'ngFinalPreparation',
  NG_INTERVENTION_OPERATION = 'ngInterventionOperation',
  NG_INTERVENTION_CLOSE_OUT = 'ngInterventionCloseOut',
  // Angola
  AG_ENGINEERING_WORK = 'agEngineeringWork',
  AG_FINAL_WIR = 'agFinalWir',
  AG_FINAL_PREPARATION = 'agFinalPreparation',
  AG_INTERVENTION_OPERATION = 'agInterventionOperation',
  AG_INTERVENTION_CLOSE_OUT = 'agInterventionCloseOut',
  // Congo
  CG_WIR_CREATED = 'cgWirCreated',
  CG_KICK_OFF_TECHNICAL_WIR = 'cgKickOffTechnicalWir',
  CG_ENGINEERING_WORK = 'cgEngineeringWork',
  CG_FINAL_WIR = 'cgFinalWir',
  CG_FINAL_PREPARATION = 'cgFinalPreparation',
  CG_INTERVENTION_OPERATION = 'cgInterventionOperation',
  CG_INTERVENTION_CLOSE_OUT = 'cgInterventionCloseOut',
  // Iraq
  IRAQ_ENGINEERING_WORK = 'iraqEngineeringWork',
  IRAQ_FINAL_WIR = 'iraqFinalWir',
  IRAQ_FINAL_PREPARATION = 'iraqFinalPreparation',
  IRAQ_INTERVENTION_OPERATION = 'iraqInterventionOperation',
  IRAQ_INTERVENTION_CLOSE_OUT = 'iraqInterventionCloseOut',
  // Argentina
  ARGENTINA_ENGINEERING_WORK = 'argentinaEngineeringWork',
  ARGENTINA_FINAL_WIR = 'argentinaFinalWir',
  ARGENTINA_FINAL_PREPARATION = 'argentinaFinalPreparation',
  ARGENTINA_INTERVENTION_OPERATION = 'argentinaInterventionOperation',
  ARGENTINA_INTERVENTION_CLOSE_OUT = 'argentinaInterventionCloseOut',
}

export type Phase = (typeof WIRCreateJourneyStepsKeys)[keyof typeof WIRCreateJourneyStepsKeys];

export enum WIRStepsKeyUK {
  WIR_CREATED = 'wirCreated',
  KICK_OFF_TECHNICAL_WIR = 'kickOffTechnicalWir',
  ENGINEERING_WORK = 'engineeringWork',
  FINAL_WIR = 'finalWir',
  FINAL_PREPARATION = 'finalPreparation',
  REACTIVE_SCOPE = 'reactiveScope',
  INTERVENTION_OPERATION = 'interventionOperation',
  INTERVENTION_CLOSE_OUT = 'interventionCloseOut',
}

export type PhaseUK = (typeof WIRStepsKeyUK)[keyof typeof WIRStepsKeyUK];

export enum WIRStepsKeyNG {
  WIR_CREATED = 'wirCreated',
  KICK_OFF_TECHNICAL_WIR = 'kickOffTechnicalWir',
  ENGINEERING_WORK = 'engineeringWork',
  FINAL_WIR = 'finalWir',
  NG_FINAL_PREPARATION = 'ngFinalPreparation',
  REACTIVE_SCOPE = 'reactiveScope',
  NG_INTERVENTION_OPERATION = 'ngInterventionOperation',
  INTERVENTION_CLOSE_OUT = 'ngInterventionCloseOut',
}

export type PhaseNG = (typeof WIRStepsKeyNG)[keyof typeof WIRStepsKeyNG];

export enum WIRStepsKeyIr {
  WIR_CREATED = 'wirCreated',
  KICK_OFF_TECHNICAL_WIR = 'kickOffTechnicalWir',
  IRAQ_ENGINEERING_WORK = 'iraqEngineeringWork',
  IRAQ_FINAL_WIR = 'iraqFinalWir',
  IRAQ_FINAL_PREPARATION = 'iraqFinalPreparation',
  REACTIVE_SCOPE = 'reactiveScope',
  IRAQ_INTERVENTION_OPERATION = 'iraqInterventionOperation',
  IRAQ_INTERVENTION_CLOSE_OUT = 'iraqInterventionCloseOut',
}

export type PhaseIr = (typeof WIRStepsKeyIr)[keyof typeof WIRStepsKeyIr];

export enum WIRStepsKeyDK {
  STAGE_GATE_1 = 'stageGate1',
  STAGE_GATE_2 = 'stageGate2',
  STAGE_GATE_3 = 'stageGate3',
  STAGE_GATE_4 = 'stageGate4',
  STAGE_GATE_5 = 'stageGate5',
}

export type PhaseDK = (typeof WIRStepsKeyDK)[keyof typeof WIRStepsKeyDK];

export const WIRCreatedSections = {
  initialRequestInformation: 'Intervention Need Identified',
  organiseKickOffMeeting: 'Organise Kick Off Meeting',
} as const;

export type WIRCreatedSectionKey = keyof typeof WIRCreatedSections;

export const WIRKofMeetingTechnicalWirSections = {
  budget: 'Budget',
  wellInformation: 'Well Information',
  organiseKickOffMeeting: 'Organise Kick Off Meeting',
  reviewWellHistory: 'Review Well History, Previous Lessons & Perform Kick off Meeting',
  reviewChecklistActions: 'Prepare Checklist & Create Actions',
  signOffTechnicalWIR: 'Technical Sign Off',
} as const;

export type WIRKofMeetingTechnicalWirSectionKey = keyof typeof WIRKofMeetingTechnicalWirSections;

export const WIREngineeringWorkSections = {
  vendorEngagementSelection: 'Vendor Engagement & Selection',
  equipmentIdentification: 'Resource Identification',
  aroRlo: 'ARO/RLO',
  engineeringAnalysis: 'Engineering Analysis',
  equipmentProcurement: 'Equipment Procurement',
  reviewChecklistActions: 'Review Checklist & Update Actions',
  interventionStrategyDefined: 'Intervention Strategy Defined',
  chemicalChecksTopsideMetallurgy: 'Chemical Checks Against Well/Topside Metallurgy',
  crossChemicalCompatibility: 'Cross Chemical Compatibility',
  chemicalChecksReservoir: 'Chemical Checks vs Reservoir Fluids',
  chemicalChecksFormation: 'Chemical Checks on the Formation',
  chemicalChecksTopsideEffluent:
    'Chemical Compatibility with Topside Effluent (From Multiple Wells)',
  chemicalChecksFlowBack: 'Flowback of Chemicals Checked Against Any Pipeline Restrictions',
  cefasRegistration: 'CEFAS Registration of Chemical',
  sandingStudy: 'Sanding Study/Check/Mitigation Analysis',
  technicalReviewRiskReward: 'Technical Peer review, Risk/Reward & Final Strategy',
  technicalPosEstabilished: 'Technical PoS Established',
  hazopCarriedOut: 'HAZOP carried out?',
  afeCreated: 'AFE Created',
  radioactiveSourceRequirement: 'Radioactive Source Requirement',
  structuralSupport: 'Structural Support Requirement',
  liftingSupport: 'Lifting Support Requirement',
  assetSideEngAss: 'Asset Site Engineering Assessment',
  programDraft: 'Program Draft',
} as const;

export type WIREngineeringWorkSectionKey = keyof typeof WIREngineeringWorkSections;

export enum WIRAFE {
  AFE_APPROVED = 'afeApproved',
  AFE_CREATED = 'afeCreated',
}

export type WIRAFEKey = (typeof WIRAFE)[keyof typeof WIRAFE];

export const WIRFinalWIRSections = {
  managerialSummary: 'Managerial Summary',
  assignInterventionSPDT: 'Assign Intervention SPDT',
  finalWirSignOff: 'Final WIR Sign Off',
} as const;

export type WIRFinalWIRSectionKey = keyof typeof WIRFinalWIRSections;

export const WIRFinalPreparationSections = {
  afeApproved: 'AFE Approved',
  regularPermitsRequested: 'Regulator Permits Requested',
  partnersNotified: 'Partners Notified',
  updateChemicalPermits: 'Update Chemical Permits Required',
  wonsApplication: 'WONS Application',
  hseNotification: 'HSE Notification',
  fpsWaiver: 'FPS Waiver',
  bridgingSimopsDocuments: 'Bridging/SIMOPS Documents',
  // draftProgrammePrepared: 'Draft Programme Prepared', //Commented on latest requirement
  wellInterventionOnPaper: 'Well Intervention on Paper',
  perforationProforma: 'Perforation Proforma',
  equipmentPreparation: 'Equipment Preparation/Interface Checks',
  programmeReview: 'Programme Review/Presentation',
  hqTechnicalReview: 'HQ Technical Review',
  programmeDistributed: 'Programme Signed & Distributed',
  costSheetPrepared: 'Cost Sheet Prepared',
  vesselApplication: 'Vessel Suitability Carried out',
  iwop: 'IWOP',
  simopsDossier: 'Simops Dossier',
  vesselSuitabilityInspection: 'Vessel Suitability Inspection',
  workInstructionCreated: 'Work Instruction Created',
} as const;

export type WIRFinalPreparationSectionKey = keyof typeof WIRFinalPreparationSections;

export const WIRInterventionOperationSections = {
  assetStatusCheck: 'Asset Status Check',
  servicePartnerPaRoleIdentification: 'Service Partner PA Role Identification',
  regularPermitsReceived: 'Regulator Permits Received', // Added for Nigeria
  equipmentPersonnelSite: 'Equipment & Personnel to Site',
  wellControllChecklistIpad: 'Well Control Checklist Completion on Ipad',
  wellHandover: 'Well Handover',
  wellHandoverIntervStart: 'Well Handover to Intervention Start',
  wellHandoverBackToOpsEnd: 'Well Handover Back to Ops End',
  emergencyDrill: 'Emergency Drill after Equipment & Personnel to Site',
  livePerformanceTracking: 'Live Operation Tracking',
  capPortalUpdates: 'CAP Portal Updates',
  prepareTpfr: 'Non-Conformance/GRAL',
  lessonsCaptured: 'Lessons Captured',
  wellHandoverFoToDt: 'well Handover FO to DT',
  wellHandoverDtToFo: 'well Handover DT to FO',
  dailyReportDistributionList: 'Daily Report Distribution List',
  // liveWcrsReporting: 'Live WCRS Reporting', // Removed as per updated requirement
} as const;

export type WIRInterventionOperationSectionKey = keyof typeof WIRInterventionOperationSections;

export const WIRInterventionCloseOutSections = {
  interventionRetrospective: 'Intervention Retrospective',
  closeOutChecklists: 'Close Out Checklist',
  interventionSingleSlideSummary: 'Intervention Single Slide Summary',
  capPortalCloseOut: 'CAP Portal Close Out',
  wonsCloseOut: 'WONS Close Out',
  signOffWcrs: 'Sign Off WCRS',
  lessonsCaptured: 'Lessons Captured',
  completionSchematicUpdate: 'Completion Schematic Updated',
  wellSchematicUpdate: 'Well Schematic Updated',
  emtraxUpdate: 'EMTRAX Updated',
  interventionWellDataStored: 'Well Intervention Data Stored',
  vendorCloseOut: 'Vendor Close Out',
  interventionPerformanceCloseOut: 'Intervention Performance Close Out',
  assetCloseOutPresentation: 'Asset Close Out Presentation',
  rex: 'REX',
  costConsolidationCloseOut: 'Cost Consolidation & Close Out',
  endOfInterventionReport: 'End of Intervention Form',
  filesAddedToSharePoint: 'Files Added to SharePoint',
  actualGhgEmission: 'Actual GHG Emission',
  hqTechnicalfinalInterventionSignOff: 'Final Intervention Sign Off',
} as const;

export type WIRInterventionCloseOutSectionKey = keyof typeof WIRInterventionCloseOutSections;

export const WIRReactiveScopeSections = {
  initialRequestInformation: 'Intervention Need Identified',
  organiseKickOffMeeting: 'Organise Kick Off Meeting',
  reviewChecklistActions: 'Complete Checklist & Actions',
  wellInformation: 'Well Information',
  vendorEngagementSelection: 'Vendor Engagement & Selection',
  programDraft: 'Program Draft',
  radioactiveSourceRequirement: 'Radioactive Source Requirement',
  structuralSupport: 'Structural Support Requirement',
  liftingSupport: 'Lifting Support Requirement',
  assetSideEngAss: 'Asset Side Engineering Assessment',
  equipmentIdentification: 'Resource Identification',
  interventionStrategyDefined: 'Intervention Strategy Defined',
  hazopCarriedOut: 'HAZOP carried out?',
  afeApproved: 'AFE Approved',
  managerialSummary: 'Managerial Summary',
  vesselApplication: 'Vessel Suitability Carried out',
  equipmentPreparation: 'Equipment Preparation/Interface Checks',
  programmeReview: 'Programme Review/Presentation',
  programmeDistributed: 'Programme Signed & Distributed',
  workInstructionCreated: 'Work Instruction Created',
  chemicalChecksTopsideMetallurgy: 'Chemical Checks Against Well/Topside Metallurgy',
  crossChemicalCompatibility: 'Cross Chemical Compatibility',
  chemicalChecksReservoir: 'Chemical Checks vs Reservoir Fluids',
  chemicalChecksFormation: 'Chemical Checks on the Formation',
  chemicalChecksTopsideEffluent:
    'Chemical Compatibility with Topside Effluent (From Multiple Wells)',
  chemicalChecksFlowBack: 'Flowback of Chemicals Checked Against Any Pipeline Restrictions',
  cefasRegistration: 'CEFAS Registration of Chemical',
  sandingStudy: 'Sanding Study/Check/Mitigation Analysis',
  updateChemicalPermits: 'Update Chemical Permits Required',
  wonsApplication: 'WONS Application',
  hseNotification: 'HSE Notification',
  fpsWaiver: 'FPS Waiver',
  bridgingSimopsDocuments: 'Bridging/SIMOPS Documents',
  perforationProforma: 'Perforation Proforma',
  finalWirSignOff: 'Final WIR Sign Off',
} as const;

export type WIRReactiveScopeSectionKey = keyof typeof WIRReactiveScopeSections;

export const WIRStageGate1Sections = {
  interventionJustification: 'Intervention Justification',
} as const;

export type WIRStageGate1SectionKey = keyof typeof WIRStageGate1Sections;

export const WIRStageGate2Sections = {
  initialRequestInformation: 'Intervention Need Identified',
  organiseKickOffMeeting: 'Organise Kick Off Meeting',
  wellInformation: 'Well Information',
  reviewWellHistory: 'Review Well History & Kick off Meeting',
  reviewChecklistActions: 'Prepare Checklist & Create Actions',
  signOffTechnicalWIR: 'Technical Sign Off',
} as const;

export type WIRStageGate2SectionKey = keyof typeof WIRStageGate2Sections;

export const WIRStageGate3Sections = {
  equipmentIdentification: 'Resource Identification',
  vendorEngagementSelection: 'Vendor Engagement & Selection',
  interventionStrategyDefined: 'Intervention Strategy Defined',
  technicalReviewRiskReward: 'Technical Peer Review & Risk/Reward',
  technicalPosEstabilished: 'Technical PoS Established',
  afeCreated: 'AFE Created',
  afeGhgEmission: 'AFE GHG Emission',
  partnersNotified: 'Partners Notified',
  afeApproved: 'AFE Approved',
  wellInterventionOnPaper: 'Well Intervention on Paper',
  equipmentPreparation: 'Equipment Preparation/Interface Checks',
  hqTechnicalReview: 'HQ Technical Review',
  programmeReview: 'Programme Review/Presentation',
  costSheetPrepared: 'Cost Sheet Prepared',
  programmeDistributed: 'Programme Signed & Distributed',
  sandingStudy: 'Sanding Study/Check/Mitigation Analysis',
  vesselApplication: 'Vessel Suitability Carried out',
  reviewChecklistActions: 'Review of Checklist & Update Actions',
  managerialSummary: 'Managerial Summary',
  wonsApplication: 'WONS Application', // added for local authoroties
  fpsWaiver: 'FPS Waiver',
  bridgingSimopsDocuments: 'Bridging/SIMOPS Documents',
  hseNotification: 'HSE Notification',
  perforationProforma: 'Perforation Proforma',
  chemicalChecksTopsideMetallurgy: 'Chemical Checks Against Well/Topside Metallurgy',
  crossChemicalCompatibility: 'Cross Chemical Compatibility',
  chemicalChecksReservoir: 'Chemical Checks vs Reservoir Fluids',
  chemicalChecksFormation: 'Chemical Checks on the Formation',
  chemicalChecksTopsideEffluent: 'Chemical Compatibility with Topside Effluent (From Multiple Wells)',
  chemicalChecksFlowBack: 'Flowback of Chemicals Checked Against Any Pipeline Restrictions',
  cefasRegistration: 'CEFAS Registration of Chemical',
  updateChemicalPermits: 'Update Chemical Permits Required',
  finalWirSignOff: 'Final WIR Sign Off',
} as const;

export type WIRStageGate3SectionKey = keyof typeof WIRStageGate3Sections;

export const WIRStageGate4Sections = {
  equipmentPersonnelSite: 'Equipment & Personnel to Site',
  wellHandoverIntervStart: 'Well Handover to Intervention Start',
  emergencyDrill: 'Emergency Drill after Equipment & Personnel to Site',
  lessonsCaptured: 'Lessons Captured',
  prepareTpfr: 'Prepare TPFR (Total Performance & Feedback Report)',
  livePerformanceTracking: 'Live Operation Tracking',
  wellHandoverBackToOpsEnd: 'Well Handover Back to Ops End',
  wellHandover: 'Well Handover',
} as const;

export type WIRStageGate4SectionKey = keyof typeof WIRStageGate4Sections;

export const WIRStageGate5Sections = {
  interventionRetrospective: 'Intervention Retrospective',
  closeOutChecklists: 'Close Out Checklist',
  interventionSingleSlideSummary: 'Intervention Single Slide Summary',
  wonsCloseOut: 'WONS Close Out',
  completionSchematicUpdate: 'Completion Schematic Updated',
  wellSchematicUpdate: 'Well Schematic Updated',
  interventionWellDataStored: 'Intervention in Well Data Stored',
  vendorCloseOut: 'Vendor Close Out',
  interventionPerformanceCloseOut: 'Intervention Performance Close Out',
  assetCloseOutPresentation: 'Asset Close Out Presentation',
  rex: 'REX',
  costConsolidationCloseOut: 'Cost Consolidation & Close Out',
  endOfInterventionReport: 'End of Intervention Report',
  hqTechnicalfinalInterventionSignOff: 'Final Intervention Sign Off',
} as const;

export type WIRStageGate5SectionKey = keyof typeof WIRStageGate5Sections;

export type Section =
  // united Kingdom
  | WIRCreatedSectionKey
  | WIRKofMeetingTechnicalWirSectionKey
  | WIREngineeringWorkSectionKey
  | WIRFinalWIRSectionKey
  | WIRFinalPreparationSectionKey
  | WIRInterventionOperationSectionKey
  | WIRInterventionCloseOutSectionKey
  | WIRReactiveScopeSectionKey
  // Denmark
  | WIRStageGate1SectionKey
  | WIRStageGate2SectionKey
  | WIRStageGate3SectionKey
  | WIRStageGate4SectionKey
  | WIRStageGate5SectionKey;
// NIGERIA

export enum WIRChecklistTypes {
  GENERIC = 'generic',
  PRODUCTION_AND_RESERVIOR = 'production_and_reservoir',
  PUMPING_OPERATION = 'pumping_operation',
  PERFORATIONS = 'perforations',
  WELL_STIMULATION = 'well_stimulation',
  CT_OPERATION = 'ct_operation',
}

export const ENGINEERING_WORK_ACTION_LABEL = 'Engineering Work';

export const STAGE_GATE_3_ACTION_LABEL = 'Stage Gate 3';

export const STAGE_GATE_5_ACTION_LABEL = 'Stage Gate 5';

export const FINAL_PREPARATION_ACTION_LABEL = 'Final Preparation';

export const INTERVENTION_CLOSE_OUT_ACTION_LABEL = 'Intervention Close Out';

export const REACTIVE_SCOPE_ACTION_LABEL = 'Reactive Scope';

export enum RestorationInterventionType {
  XMAS_TREE_WELL_HEAD_REPAIR = 'Xmas Tree / Wellhead Repair',
  DOWNHOLE_PUMP_REPLACEMENT_ROD_REPAIR = 'Downhole pump Replacement or Rod repair',
  TUBING_CHANGEOUT_RECOMPLETION_PATCH = 'Tubing changeout, recompletion, patch/straddle',
  DHSV_INSTALLATION_REPAIRS = 'DHSV installation or repairs/changeout',
  WELL_PLUGGING_UNPLUGGING = 'Well Plugging / Unplugging',
  OTHER_NOT_DEFINED_RESTORATION = 'Other not defined',
}

export enum SafeGuardingInterventionType {
  SCALE_SQUEEZE = 'Scale Squeeze',
  TUBING_CLEAN_OUT = 'Tubing/Liner clean out',
  WATER_WASH = 'Water wash / solvent treatment',
  FOAM_LIFTING = 'Foam lifting / Well Rocking',
  OTHER_NOT_DEFINED_SAFEGUARDING = 'Other not defined',
}

export enum OptimisationInterventionType {
  ACID_STIMULATION = 'Acid Stimulation',
  DOWNHOLE_PUMP_OPTIMISATION = 'Downhole pump optimisation or retrofitting',
  GAS_LIFT_OPTIMISATION = 'Gas lift optimisation or retrofit',
  HYDRAULIC_FRACTURING = 'Hydraulic Fracturing',
  ADD_PERFORATIONS = 'Re/add perforations',
  SAND_CONTROL_RETROFIT = 'Sand control retrofit/changeout/repair',
  VELOCITY_STRINGS = 'Velocity Strings or talipipe extensions',
  WATER_SHUT_OFF = 'Water/Gas shut off',
  ZONE_CHANGE = 'Zone change',
  MECHANICAL_SCALE_SCRAPING = 'Mechanical Scale Scraping',
  OTHER_NOT_DEFINED_OPTIMISATION = 'Other not defined',
}

export enum SurveillanceInterventionType {
  PLT = 'PLT(Production Logging Tool)',
  SGS = 'SGS(Static Gradient Survey)',
  DOWNHOLE_SAMPLING = 'Downhole sampling',
  HUD_INVESTIGATION = 'HUD/Access Investigation (Caliper, drift)',
  CEMENT_QUALITY_CHECKS = 'Cement/Tubular/mutistring quality checks',
  OVERBURDEN_MONITORING = 'Overburden monitoring',
  FLOWING_GRADIANT_SURVEY = 'FGS (Flowing Gradiant Survey)',
  OTHER_NOT_DEFINED_SURVEILLANCE = 'Other not defined',
}

export const InterventionType = {
  ...RestorationInterventionType,
  ...SafeGuardingInterventionType,
  ...OptimisationInterventionType,
  ...SurveillanceInterventionType,
} as const;

export type InterventionTypeKey = keyof typeof InterventionType;

export const NOT_APPLICABLE = 'N/A';

export enum WirIntervSelectTabs {
  REQUEST_DETAILS_LABEL = 'Request Details',
  BUDGET_LABEL = 'Budget',
  IMPACT_LABEL = 'Impact',
}

export enum WirCreateTabs {
  ACTIVITY_LABEL = 'activity',
  SUMMARY_LABEL = 'summary',
  COMMENT_LABEL = 'comment',
  INFO_LABEL = 'Info',
  DOWNLOAD_PDF = 'DownloadPDF',
}

export type WirCreateTab = (typeof WirCreateTabs)[keyof typeof WirCreateTabs];

export enum Units {
  POUND = '£',
  DAYS = 'days',
  PERCENT = '%',
  BBLPERDAY = 'bbl/d',
  BOE = 'BOE',
  BOEPD = 'BOEPD',
  MTCO2e = 'MTCO2e',
  US_DOLLER = '$',
  EURO = '€',
  DKK = 'DKK',
}

export type Unit = (typeof Units)[keyof typeof Units];

export const YES = 'Yes';

export const NO = 'No';

export const GENERIC_HSE_NOTIFICATION_CHECK_ID = 1;

export const ROUTINE_4_REACTIVE_TYPE = 'ROUTINE_4_REACTIVE';

export enum WirInterventionTabs {
  LIST = 'list',
  SCHEDULE = 'schedule',
  STATISTICS = 'statistics',
}

export type WirInterventionTab = (typeof WirInterventionTabs)[keyof typeof WirInterventionTabs];

export enum StatusTypes {
  DELAYED = 'Delayed',
  ON_TRACK = 'On-Track',
  BEHIND_SCHEDULE = 'Behind Schedule',
  AT_RISK = 'At Risk',
  COMPLETED = 'Completed',
}

export enum InterventionArchiveReasons {
  COMPLETED = 'Intervention completed',
  REMOVED_FROM_PLAN = 'Intervention cancelled incomplete: Removed from plan',
  DID_NOT_PASS_FINAL_APPROVAL = 'Intervention cancelled incomplete: Intervention did not pass final approval',
  NO_LONGER_REQUIRED = 'WIR no longer required (other reason)',
}

export const ATTACHMENT_OR_COMMENT_REQUIRED_SECTIONS = [
  'reviewWellHistory',
  'vendorEngagementSelection',
  'equipmentIdentification',
  'aroRlo',
  'programDraft',
  'interventionStrategyDefined',
  'technicalPosEstabilished',
  'partnersNotified',
  'afeGhgEmission',
  'actualGhgEmission',
  // 'draftProgrammePrepared',
  'equipmentPreparation',
  'assetStatusCheck',
  'equipmentPersonnelSite',
  'capPortalUpdates',
  'lessonsCaptured',
  'prepareTpfr',
  // 'liveWcrsReporting', // Removed as updated requirement
  'costSheetPrepared',
  'endOfInterventionReport',
  'capPortalCloseOut',
  'regularPermitsRequested',
  'regularPermitsReceived',
  'wellControllChecklistIpad',
  'radioactiveSourceRequirement',
  'structuralSupport',
  'liftingSupport',
  'assetSideEngAss',
] as const;

export type AttachmentOrCommentRequiredSection =
  (typeof ATTACHMENT_OR_COMMENT_REQUIRED_SECTIONS)[number];

export const ATTACHMENT_AND_COMMENT_REQUIRED_SECTIONS = [
  'technicalReviewRiskReward',
  'equipmentProcurement',
  'afeCreated',
  'afeApproved',
  'wellInterventionOnPaper',
  'hazopCarriedOut',
  'programmeReview',
  'programmeDistributed',
  'livePerformanceTracking',
  'interventionRetrospective',
  'interventionSingleSlideSummary',
  'signOffWcrs',
  'wellSchematicUpdate',
  'emtraxUpdate',
  'vendorCloseOut',
  'interventionWellDataStored',
  'interventionPerformanceCloseOut',
  'assetCloseOutPresentation',
  'rex',
  'costConsolidationCloseOut',
  'hqTechnicalReview',
] as const;

export type AttachmentAndCommentRequiredSection =
  (typeof ATTACHMENT_AND_COMMENT_REQUIRED_SECTIONS)[number];

export const MULTI_LINE_ROWS = 12;

export enum BudgetClassification {
  ROUTINE_OPEX = 'Routine Opex',
  NON_ROUTINE_OPEX = 'Non Routine Opex',
  CAPEX = 'Capex',
}

export const DocumentLink = {
  technicalPosEstabilished:
    'https://abzll.main.glb.corp.local/abzlivelink/llisapi.dll/app/nodes/111057918',
  afeCreated: 'https://abzll.main.glb.corp.local/abzlivelink/llisapi.dll/app/nodes/111057671',
  wonsApplication: 'https://abzll.main.glb.corp.local/abzlivelink/llisapi.dll/app/nodes/111065178',
  hseNotification: 'https://abzll.main.glb.corp.local/abzlivelink/llisapi.dll/app/nodes/111064963',
  hseNotificationWellOps:
    'https://abzll.main.glb.corp.local/abzlivelink/llisapi.dll/app/nodes/111065185',
  updateChemicalPermits:
    'https://tepukcms.gb.ep.corp.local/UserSuite/View/Download/e0f032e6-cbd6-e811-8693-9c8e99faa732',
  updateChemicalPermitsTracker:
    'https://totalworkplace.sharepoint.com/:x:/r/sites/TEPUKDrillingWellsDepartment/TEPUK%20Permit%20Tracker/Permit%20Tracker%20FINAL%20Rev%201.xlsx?d=w9f9ed6e7d9df4f08bc17cf9fab3f101c&csf=1&web=1&e=yDwTX0',
  programmeReview:
    'https://tepukcms.main.glb.corp.local/UserSuite/Document/c5e969a8-538a-468e-a9b5-db89d39072db',
  emergencyDrill:
    'https://tepukcms.main.glb.corp.local/UserSuite/Document/2de93e2f-2300-40a5-a5ed-830d1e374344',
  emergencyDrillWTI:
    'https://tepukcms.main.glb.corp.local/UserSuite/Document/f7db8f77-3fef-ed11-a2e9-005056a50e5c',
  wellHandoverIntervStart:
    'https://tepukcms.main.glb.corp.local/UserSuite/Document/120c7da2-2776-4914-9c1a-d6fc9c857861',
  wellHandoverIntervStartDWFORM:
    'https://tepukcms.main.glb.corp.local/UserSuite/Document/b098b859-a757-46ac-9ebe-c414d73d2faf',
  programmeCreated: 'https://',
  workInstructionCreated: 'https://',
};

export type DocumentLinks = { link: string; name: string }[];

export const WIRPhaseSectionApiKeys = {
  signOffTechnicalWIR: 'SIGN_OFF_TECHNICAL_WIR',
  reviewWellHistory: 'REVIEW_WELL_HISTORY',
  vendorEngagementSelection: 'VENDOR_ENGAGEMENT',
  aroRlo: 'ARO_RLO',
  programDraft: 'PROGRAM_DRAFT',
  interventionStrategyDefined: 'INTERVENTION_STRATEGY_DEFINED',
  technicalReviewRiskReward: 'TECHNICAL_REVIEW_RISK_REWARD',
  technicalPosEstabilished: 'TECHNICAL_POS_ESTABILISHED',
  hazopCarriedOut: 'HAZOP_CARRIED_OUT',
  equipmentProcurement: 'EQUIPMENT_PROCUREMENT',
  afeCreated: 'AFE_CREATED',
  afeGhgEmission: 'AFE_GHG_EMISSION',
  sandingStudy: 'SANDING_STUDY',
  chemicalChecksTopsideMetallurgy: 'CHEMICAL_CHECKS_TOPSIDE_METALLURGY',
  crossChemicalCompatibility: 'CROSS_CHEMICAL_COMPATIBILITY',
  chemicalChecksReservoir: 'CHEMICAL_CHECKS_RESERVOIR',
  chemicalChecksFormation: 'CHEMICAL_CHECKS_FORMATION',
  chemicalChecksTopsideEffluent: 'CHEMICAL_CHECKS_TOPSIDE_EFFLUENT',
  chemicalChecksFlowBack: 'CHEMICAL_CHECKS_FLOW_BACK',
  cefasRegistration: 'CEFAS_REGISTRATION',
  finalWirSignOff: 'SIGN_OFF',
  afeApproved: 'AFE_APPROVED',
  regularPermitsRequested: 'REGULAR_PERMITS_REQUESTED',
  partnersNotified: 'PARTNERS_NOTIFIED',
  updateChemicalPermits: 'UPDATE_CHEMICAL_PERMITS',
  wonsApplication: 'WONS_APPLICATION',
  hseNotification: 'HSE_NOTIFICATION',
  fpsWaiver: 'FPS_WAIVER',
  bridgingSimopsDocuments: 'BRIDGING_SIMOPS_DOCUMENTS',
  wellInterventionOnPaper: 'WELL_INTERVENTION_ON_PAPER',
  perforationProforma: 'PERFORATION_PROFORMA',
  equipmentPreparation: 'EQUIPMENT_PREPARATION',
  programmeReview: 'PROGRAMME_REVIEW',
  hqTechnicalReview: 'HQ_TECHNICAL_REVIEW',
  programmeDistributed: 'PROGRAMME_DISTRIBUTED',
  costSheetPrepared: 'COST_SHEET_PREPARED',
  vesselApplication: 'VESSEL_APPLICATION',
  assetStatusCheck: 'ASSET_STATUS_CHECK',
  servicePartnerPaRoleIdentification: 'SERVICE_PARTNER_PA_ROLE_IDENTIFICATION',
  regularPermitsReceived: 'REGULAR_PERMITS_RECEIVED',
  equipmentPersonnelSite: 'EQUIPMENT_AND_PERSONEL_TO_SITE',
  capPortalUpdates: 'CAP_PORTAL_UPDATE',
  wellHandoverIntervStart: 'WELL_HANDOVER_INTERV_START',
  uwimsUpdated: 'UWIMS_UPDATED',
  emergencyDrill: 'EMERGENCY_DRILL',
  wellControllChecklistIpad: 'WELL_CONTROL_CHECKLIST_IPAD',
  lessonsCaptured: 'LESSONS_CAPTURED',
  prepareTpfr: 'PREPARE_TPFR',
  livePerformanceTracking: 'LIVE_PERFORMANCE_TRACKING',
  wellHandoverBackToOpsEnd: 'WELL_HANDOVER_BACK_TO_OPS_END',
  interventionRetrospective: 'INTERVENTION_RETRO',
  interventionSingleSlideSummary: 'INTERVENTION_SINGLE_SLIDE',
  capPortalCloseOut: 'CAP_PORTAL_CLOSEOUT',
  wonsCloseOut: 'WONS_CLOSE_OUT',
  signOffWcrs: 'SIGN_OFF_WCRS',
  completionSchematicUpdate: 'COMPLETION_SCHEMATIC_UPDATE',
  wellSchematicUpdate: 'WELL_SCHEMATIC_UPDATE',
  emtraxUpdate: 'EMTRAX_UPDATE',
  interventionWellDataStored: 'INTERVENTION_DATA_STORED',
  vendorCloseOut: 'VENDOR_CLOSE_OUT',
  interventionPerformanceCloseOut: 'INTERVENTION_PERFORMANCE_CLOSE_OUT',
  assetCloseOutPresentation: 'ASSET_CLOSE_OUT',
  rex: 'REX',
  costConsolidationCloseOut: 'COST_CONSOLIDATION_CLOSE_OUT',
  endOfInterventionReport: 'INTERVENTION_REPORT',
  actualGhgEmission: 'ACTUAL_GHG_EMISSION',
  hqTechnicalfinalInterventionSignOff: 'FINAL_SIGN_OFF',
  interventionJustification: 'INTERVENTION_JUSTIFICATION',
  simopsDossier: 'SIMOPS_DOSSIER',
  vesselSuitabilityInspection: 'VESSEL_SUITABILILITY_INSPECTION',
  dailyReportDistributionList: 'DAILY_REPORT_DISTRIBUTION_LIST',
  wellHandoverDtToFo: 'WELL_HANDOVER_DT_TO_FO',
  wellHandoverFoToDt: 'WELL_HANDOVER_FO_TO_DT',
  workInstructionCreated: 'WORK_INSTRUCTION_CREATED',
  filesAddedToSharePoint: 'FILES_ADDED_TO_SHAREPOINT',
  radioactiveSourceRequirement: 'RADIOACTIVE_SOURCE_REQUIREMENT',
  structuralSupport: 'STRUCTUARAL_SUPPORT',
  liftingSupport: 'LIFTING_SUPPORT',
  assetSideEngAss: 'ASSET_SIDE_ENG_ASS',
};

export const WIRPhaseSectionDescription = {
  INTERVENTION_SINGLE_SLIDE: 'Single Slide Summary',
  CAP_PORTAL_CLOSEOUT: 'CAP Portal Close Out',
  SIGN_OFF_WCRS: 'Sign Off WCRS',
  WELL_SCHEMATIC_UPDATE: 'Wellhead Schematic Updated',
  EMTRAX_UPDATE: 'EMTRAX Updated',
  INTERVENTION_DATA_STORED: 'Well Data Stored/Archived',
  VENDOR_CLOSE_OUT: 'Vendor Close Out',
  ASSET_CLOSE_OUT: 'Asset Close Out Presentation',
  REX: 'REX Submitted',
  COST_CONSOLIDATION_CLOSE_OUT: 'Cost Consolidation & Close Out',
  // WONS_CLOSE_OUT: 'WONS Close Out',
  INTERVENTION_PERFORMANCE_CLOSE_OUT: 'Intervention Performance Close Out',
  COMPLETION_SCHEMATIC_UPDATE: 'Completion Schematic Updated',
  LESSONS_CAPTURED: 'Lessons Captured',
  UWIMS_UPDATED: 'UWIMS Updated',
  WELL_HANDOVER_BACK_TO_OPS_END: 'Well Handover Back To Ops End',
  WELL_HANDOVER_INTERV_START: 'Well Handover Intervention Start',
};

export type WIRSections =
  | typeof WIRKofMeetingTechnicalWirSections
  | typeof WIRFinalWIRSections
  | typeof WIRStageGate3Sections
  | typeof WIREngineeringWorkSections
  | typeof WIRFinalPreparationSections
  | typeof WIRStageGate5Sections
  | typeof WIRInterventionCloseOutSections
  | typeof WIRStageGate4Sections
  | typeof WIRInterventionOperationSections
  | typeof WIRStageGate1Sections
  | typeof WIRStageGate2Sections;

export type WIRSectionKeys = typeof WIRPhaseSectionApiKeys;

export const UK_STEP_KEYS = [
  WIRCreateJourneyStepsKeys.WIR_CREATED,
  WIRCreateJourneyStepsKeys.KICK_OFF_TECHNICAL_WIR,
  WIRCreateJourneyStepsKeys.ENGINEERING_WORK,
  WIRCreateJourneyStepsKeys.FINAL_WIR,
  WIRCreateJourneyStepsKeys.FINAL_PREPARATION,
  WIRCreateJourneyStepsKeys.INTERVENTION_OPERATION,
  WIRCreateJourneyStepsKeys.INTERVENTION_CLOSE_OUT,
];

export const UK_REACTIVE_SCOPE_STEP_KEYS = [
  WIRCreateJourneyStepsKeys.REACTIVE_SCOPE,
  WIRCreateJourneyStepsKeys.INTERVENTION_OPERATION,
  WIRCreateJourneyStepsKeys.INTERVENTION_CLOSE_OUT,
];

export const DK_STEP_KEYS = [
  WIRCreateJourneyStepsKeys.STAGE_GATE_1,
  WIRCreateJourneyStepsKeys.STAGE_GATE_2,
  WIRCreateJourneyStepsKeys.STAGE_GATE_3,
  WIRCreateJourneyStepsKeys.STAGE_GATE_4,
  WIRCreateJourneyStepsKeys.STAGE_GATE_5,
];

export const DK_REACTIVE_SCOPE_STEP_KEYS = [
  WIRCreateJourneyStepsKeys.REACTIVE_SCOPE,
  WIRCreateJourneyStepsKeys.STAGE_GATE_4,
  WIRCreateJourneyStepsKeys.STAGE_GATE_5,
];

export const NG_STEP_KEYS = [
  WIRCreateJourneyStepsKeys.WIR_CREATED,
  WIRCreateJourneyStepsKeys.KICK_OFF_TECHNICAL_WIR,
  WIRCreateJourneyStepsKeys.ENGINEERING_WORK,
  WIRCreateJourneyStepsKeys.FINAL_WIR,
  WIRCreateJourneyStepsKeys.NG_FINAL_PREPARATION,
  WIRCreateJourneyStepsKeys.NG_INTERVENTION_OPERATION,
  WIRCreateJourneyStepsKeys.NG_INTERVENTION_CLOSE_OUT,
];

export const NG_REACTIVE_SCOPE_STEP_KEYS = [
  WIRCreateJourneyStepsKeys.REACTIVE_SCOPE,
  WIRCreateJourneyStepsKeys.NG_INTERVENTION_OPERATION,
  WIRCreateJourneyStepsKeys.NG_INTERVENTION_CLOSE_OUT,
];

export const IR_STEP_KEYS = [
  WIRCreateJourneyStepsKeys.WIR_CREATED,
  WIRCreateJourneyStepsKeys.KICK_OFF_TECHNICAL_WIR,
  WIRCreateJourneyStepsKeys.IRAQ_ENGINEERING_WORK,
  WIRCreateJourneyStepsKeys.IRAQ_FINAL_WIR,
  WIRCreateJourneyStepsKeys.IRAQ_FINAL_PREPARATION,
  WIRCreateJourneyStepsKeys.IRAQ_INTERVENTION_OPERATION,
  WIRCreateJourneyStepsKeys.IRAQ_INTERVENTION_CLOSE_OUT,
];

export const AR_STEP_KEYS = [
  WIRCreateJourneyStepsKeys.WIR_CREATED,
  WIRCreateJourneyStepsKeys.KICK_OFF_TECHNICAL_WIR,
  WIRCreateJourneyStepsKeys.ARGENTINA_ENGINEERING_WORK,
  WIRCreateJourneyStepsKeys.ARGENTINA_FINAL_WIR,
  WIRCreateJourneyStepsKeys.ARGENTINA_FINAL_PREPARATION,
  WIRCreateJourneyStepsKeys.ARGENTINA_INTERVENTION_OPERATION,
  WIRCreateJourneyStepsKeys.ARGENTINA_INTERVENTION_CLOSE_OUT,
];

export const IR_REACTIVE_SCOPE_STEP_KEYS = [
  WIRCreateJourneyStepsKeys.REACTIVE_SCOPE,
  WIRCreateJourneyStepsKeys.IRAQ_INTERVENTION_OPERATION,
  WIRCreateJourneyStepsKeys.IRAQ_INTERVENTION_CLOSE_OUT,
];

export const AR_REACTIVE_SCOPE_STEP_KEYS = [
  WIRCreateJourneyStepsKeys.REACTIVE_SCOPE,
  WIRCreateJourneyStepsKeys.ARGENTINA_INTERVENTION_OPERATION,
  WIRCreateJourneyStepsKeys.ARGENTINA_INTERVENTION_CLOSE_OUT,
];

export const CG_STEP_KEYS = [
  WIRCreateJourneyStepsKeys.CG_WIR_CREATED,
  WIRCreateJourneyStepsKeys.CG_KICK_OFF_TECHNICAL_WIR,
  WIRCreateJourneyStepsKeys.CG_ENGINEERING_WORK,
  WIRCreateJourneyStepsKeys.CG_FINAL_WIR,
  WIRCreateJourneyStepsKeys.CG_FINAL_PREPARATION,
  WIRCreateJourneyStepsKeys.CG_INTERVENTION_OPERATION,
  WIRCreateJourneyStepsKeys.CG_INTERVENTION_CLOSE_OUT,
];

export const CG_REACTIVE_SCOPE_STEP_KEYS = [
  WIRCreateJourneyStepsKeys.REACTIVE_SCOPE,
  WIRCreateJourneyStepsKeys.CG_INTERVENTION_OPERATION,
  WIRCreateJourneyStepsKeys.CG_INTERVENTION_CLOSE_OUT,
];

export const AG_STEP_KEYS = [
  WIRCreateJourneyStepsKeys.WIR_CREATED,
  WIRCreateJourneyStepsKeys.KICK_OFF_TECHNICAL_WIR,
  WIRCreateJourneyStepsKeys.AG_ENGINEERING_WORK,
  WIRCreateJourneyStepsKeys.AG_FINAL_WIR,
  WIRCreateJourneyStepsKeys.AG_FINAL_PREPARATION,
  WIRCreateJourneyStepsKeys.AG_INTERVENTION_OPERATION,
  WIRCreateJourneyStepsKeys.AG_INTERVENTION_CLOSE_OUT,
];

export const AG_REACTIVE_SCOPE_STEP_KEYS = [
  WIRCreateJourneyStepsKeys.REACTIVE_SCOPE,
  WIRCreateJourneyStepsKeys.AG_INTERVENTION_OPERATION,
  WIRCreateJourneyStepsKeys.AG_INTERVENTION_CLOSE_OUT,
];

export const WELL_INFO_COMPLETION_DATE = '1990-01-01';

export interface PhaseMessages {
  [key: string]: string;
}

export type DueDatesDTO = {
  phase: WIRCreateJourneyStepsKeys;
  dueDate: string | undefined;
};

export const WATER_INJECTOR = 'Water Injector';

export const GAS_INJECTOR = 'Gas Injector';
